import { useClick } from '@/hooks/useClick'
import { Popover, PopoverContent, PopoverTrigger } from '@you/ui'
import { Icon } from '@/components/Icon'
import { DepartmentTreeNode } from '@/api/types/organization.type'

const TreeItem = ({
  node,
  level,
  expanded,
  onToggle,
  onSelect,
  onCreateChild,
  onDelete,
  isSelected,
  editingId,
  editingName,
  parentId,
  setEditingName,
  onStartEdit,
  onCancelEdit,
  onConfirmEdit,
}: {
  node: DepartmentTreeNode
  level: number
  expanded: Record<string, boolean>
  onToggle: (id: string) => void
  onSelect: (node: DepartmentTreeNode) => void
  onCreateChild?: (params: {
    id?: string
    name?: string
    parentId?: string
  }) => void
  onDelete?: (id: string) => void
  isSelected: boolean
  editingId?: string
  editingName: string
  parentId: string
  setEditingName: (name: string) => void
  onStartEdit?: (id: string, name: string, parentId: string) => void
  onCancelEdit?: () => void
  onConfirmEdit?: (id: string, name: string) => void
}) => {
  const isExpanded = expanded[node.id]
  const isLeaf = !node.subDepartments || node.subDepartments.length === 0
  const paddingLeft = level === 0 ? 0 : level * 14
  const isEditing = editingId === node.id
  const { onClick, onDoubleClick } = useClick({
    onClick: () => onSelect(node),
    onDoubleClick: () => handleDoubleClick(),
  })

  const handleAddChildClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    // 如果是根节点（level === 0），parentId 应该是 "0"
    // 如果是更深层级，parentId 是当前节点的 id
    const actualParentId = level === 0 ? '0' : node.id
    onCreateChild?.({ id: '', name: '', parentId: actualParentId })
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete?.(node.id)
  }

  const handleDoubleClick = () => {
    if (!node.isRoot) {
      onStartEdit?.(node.id, node.name, parentId)
    }
  }

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCancelEdit?.()
  }

  const handleConfirmClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onConfirmEdit?.(node.id, editingName)
  }

  return (
    <div
      className={`group flex items-center justify-between cursor-pointer select-none transition-all duration-300 ease-in-out ${
        isSelected
          ? 'bg-[#F7F1FF] text-[#8752FF]'
          : isLeaf
          ? 'hover:bg-[#F7F1FF] hover:text-[#8752FF]'
          : ''
      }`}
      style={{ paddingLeft }}
    >
      <div
        className="flex items-center w-full rounded-md px-2 py-2 text-[16px] transition-all duration-300 ease-in-out"
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        style={{
          color: isSelected ? '#8752FF' : '#555555',
        }}
      >
        {!isLeaf ? (
          <button
            title="展开"
            className={`flex-none shrink-0 inline-flex items-center justify-center transition-colors duration-300 ease-in-out ${
              isSelected ? 'text-[#8752FF]' : 'text-[#555555]'
            }`}
            onClick={(e) => {
              e.stopPropagation()
              onToggle(node.id)
            }}
          >
            <Icon
              name="arrowRight"
              className={
                'w-[18px] h-[18px] transition-transform duration-300 ease-in-out ' +
                (isExpanded ? 'rotate-90' : '')
              }
            />
          </button>
        ) : (
          <div className="w-[18px] h-[18px]"></div>
        )}
        {isEditing ? (
          <>
            <input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              className="flex-1 text-[16px] border-0 rounded focus:outline-none focus:ring-0 bg-transparent min-w-0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleConfirmClick(e as unknown as React.MouseEvent)
                } else if (e.key === 'Escape') {
                  handleCancelClick(e as unknown as React.MouseEvent)
                }
              }}
            />
            <div className="flex items-center space-x-1 px-1 flex-none">
              <button className="p-1" onClick={handleCancelClick} title="取消">
                <Icon name="close" className="w-4 h-4" />
              </button>
              <button className="p-1" onClick={handleConfirmClick} title="确认">
                <Icon name="check" className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="flex-1 rounded-md" style={{ padding: '4px' }}>
              {node.name}
            </span>
            <div className="relative">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    title="更多"
                    className={`pr-2 transition-colors duration-300 ease-in-out ${
                      isSelected ? 'text-[#8752FF]' : 'text-[#555555]'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Icon name="option" className="w-[18px] h-[18px]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-40 p-2">
                  <div className="flex flex-col">
                    <button
                      className="text-left px-2 py-1.5 rounded hover:bg-[#F7F1FF] text-[#666666]"
                      onClick={handleAddChildClick}
                    >
                      新增子级
                    </button>
                    {!node.isRoot && (
                      <button
                        className="text-left px-2 py-1.5 rounded hover:bg-red-50 text-[#EE6262]"
                        onClick={handleDeleteClick}
                      >
                        删除
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default TreeItem
